/* American Premium cart drawer: real Shopify Ajax cart (/cart/add.js, /cart/change.js) with the drawer section
   re-rendered through the Section Rendering API. The header bag and a successful add open the drawer; nothing
   navigates to /cart unless JavaScript is unavailable. The drawer is a modal <dialog>, so focus stays inside it,
   Escape closes it and focus returns to the control that opened it. */
(() => {
  'use strict';
  if (window.apCart) return;
  const SECTION = document.getElementById('bag-dialog')?.dataset.sectionId || 'ap-cart-drawer';
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const dialog = () => document.getElementById('bag-dialog');
  let renders = 0;          // drawer re-renders; a reconcile attempt is recorded against the render it settled
  let settledRender = -1;   // render after which the last reconcile attempt finished (no automatic retry for it)
  let giftBlocked = false;  // gift state still pending, failed or charged: checkout stays disabled
  let tierBlocked = false;  // native subscription prices must match the selected quantity before checkout
  let stale = false;        // a change succeeded but the drawer could not be re-rendered: checkout stays disabled
  let opener = null;
  let busy = false;

  function setCount(count) {
    document.querySelectorAll('[data-bag-count]').forEach(el => {
      el.textContent = count;
      el.hidden = !Number(count);
    });
  }

  function lock(on) { document.documentElement.classList.toggle('ap-bag-open', on); }

  function open(from) {
    const d = dialog();
    if (!d) { location.href = root + 'cart'; return; }
    if (d.hasAttribute('data-cart-page')) {
      d.scrollIntoView({block: 'start'});
    } else if (!d.open) {
      opener = from || document.activeElement;
      d.showModal();
    }
    lock(!d.hasAttribute('data-cart-page'));
    // A drawer that could not be re-rendered gets one read-only refresh; checkout stays disabled until it succeeds.
    if (stale && !busy) {
      setBusy(true);
      // After a successful refresh, settle the gift state of the fresh drawer before checkout unlocks.
      refresh().then(ok => (ok ? reconcileCart() : null)).finally(() => setBusy(false));
      return;
    }
    // A cart changed elsewhere (another tab or the legacy pages) can hold a stale gift state: settle it once.
    // Never retry a state that the last attempt has already settled (or failed on).
    const plan = giftPlan(giftState());
    if ((plan?.pending || tierMismatch()) && !busy && settledRender !== renders) {
      setBusy(true);
      reconcileCart().finally(() => setBusy(false));
    }
  }

  function close() { const d = dialog(); if (d instanceof HTMLDialogElement && d.open) d.close(); }

  function showError(message, where) {
    const target = where || (dialog() && dialog().querySelector('[data-ap-cart-error]'));
    if (!target) return;
    target.textContent = message || 'Something went wrong. Please try again.';
    target.hidden = false;
  }

  // Returns true when the drawer content was replaced.
  function render(sections) {
    const html = sections && sections[SECTION];
    const d = dialog();
    if (!html || !d) return false;
    const fresh = new DOMParser().parseFromString(html, 'text/html');
    const content = fresh.querySelector('[data-ap-cart-content]');
    const freshDialog = fresh.querySelector('[data-ap-cart]');
    if (!content) return false;
    d.querySelector('[data-ap-cart-content]').innerHTML = content.innerHTML;
    renders++;
    stale = false; // only a valid fresh drawer clears the stale state
    applyControls();
    if (freshDialog) {
      d.dataset.itemCount = freshDialog.dataset.itemCount;
      setCount(freshDialog.dataset.itemCount);
    }
    return Boolean(content);
  }

  // One read-only refresh of the drawer section (never repeats a cart mutation).
  async function refresh() {
    try {
      const response = await fetch(`${dialog()?.hasAttribute('data-cart-page') ? location.pathname : root}?sections=${SECTION}`, {headers: {Accept: 'application/json'}});
      if (!response.ok) return false;
      return render(await response.json());
    } catch (_) {
      return false;
    }
  }

  async function post(path, body) {
    const response = await fetch(root + path, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({...body, sections: SECTION, sections_url: location.pathname})
    });
    let data = {};
    try { data = await response.json(); } catch (_) {}
    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || 'This item could not be added to your bag.');
      error.status = response.status;
      error.known = !!(data.description || data.message);
      throw error;
    }
    return data;
  }

  const planId = item => String(item.selling_plan_allocation?.selling_plan?.id || '');
  function adultTiers() {
    return new Map([...dialog()?.querySelectorAll('.bag-line[data-plan-one][data-plan-two]') || []]
      .map(line => [line.dataset.variant, {one: line.dataset.planOne, two: line.dataset.planTwo}]));
  }
  function tierMismatch() {
    const lines = [...dialog()?.querySelectorAll('.bag-line') || []];
    for (const [variant, tier] of adultTiers()) {
      const managed = lines.filter(line => line.dataset.variant === variant && [tier.one, tier.two].includes(line.dataset.sellingPlan));
      const quantity = managed.reduce((sum, line) => sum + Number(line.dataset.quantity), 0);
      if (managed.some(line => line.dataset.sellingPlan !== (quantity >= 2 ? tier.two : tier.one))) return true;
    }
    return false;
  }
  // Like MASI, count subscription bottles of the same variant across lines. One-time purchases and other
  // delivery schedules do not count. Change only the native plan; preserve every bottle and line property.
  async function reconcileCart() {
    tierBlocked = false;
    if (tierMismatch()) {
      try {
        const response = await fetch(root + 'cart.js', {headers: {Accept: 'application/json'}, cache: 'no-store'});
        if (!response.ok) throw new Error('Your bag could not be read.');
        let cart = await response.json();
        for (const [variant, tier] of adultTiers()) {
          const managed = () => cart.items.filter(item => String(item.variant_id) === variant && [tier.one, tier.two].includes(planId(item)));
          const lines = managed();
          const quantity = lines.reduce((sum, item) => sum + item.quantity, 0);
          const desired = quantity >= 2 ? tier.two : tier.one;
          for (let remaining = lines.length; remaining > 0; remaining--) {
            const item = managed().find(item => planId(item) !== desired);
            if (!item) break;
            // Shopify can merge identical lines and change their keys. Use each response's current line index.
            cart = await post('cart/change.js', {line: cart.items.indexOf(item) + 1, quantity: item.quantity, selling_plan: Number(desired), properties: item.properties || {}});
          }
        }
        if (!render(cart.sections) && !(await refresh())) stale = true;
      } catch (_) {
        // Read back an uncertain result. Never repeat the customer's quantity change or a failed plan mutation.
        if (!(await refresh())) stale = true;
      }
      tierBlocked = stale || tierMismatch();
    }
    await reconcileGifts();
    settledRender = renders;
    if (tierBlocked) showError('Your subscription discount could not be updated. Refresh your cart before checkout.');
    applyControls();
  }

  /* Welcome gift. The drawer section reports which gift variants the paid lines earn ("entitled") and which cart
     lines are gift lines. One line per earned gift; duplicates, orphans and charged gift lines are set to 0. Only gift
     lines are touched, never a paid line, and the customer's own request is never repeated. */
  function giftState() {
    const el = dialog() && dialog().querySelector('[data-ap-gift-state]');
    try { return el ? JSON.parse(el.textContent) : null; } catch (_) { return null; }
  }
  function giftPlan(state) {
    if (!state) return null;
    const wanted = new Map(state.entitled.filter(g => g.available && g.free).map(g => [g.id, g]));
    const updates = {};
    const have = new Set();
    let charged = false;
    for (const line of state.lines) {
      if (wanted.has(line.id) && !have.has(line.id) && line.final === 0) {
        have.add(line.id);
        if (line.quantity !== 1) updates[line.key] = 1;
      } else {
        if (line.final > 0) charged = true;
        updates[line.key] = 0;
      }
    }
    const additions = charged ? [] : [...wanted.keys()].filter(id => !have.has(id)).map(id => ({id, quantity: 1, properties: {_ap_role: 'welcome_gift'}}));
    const unavailable = state.entitled.some(g => !g.available || !g.free);
    return {updates, additions, charged, unavailable, pending: Object.keys(updates).length > 0 || additions.length > 0};
  }
  // One attempt per customer operation, then the fresh drawer state decides whether checkout may proceed.
  async function reconcileGifts() {
    const plan = giftPlan(giftState());
    if (!plan) return;
    const notice = 'Your formulas are in your bag, but the welcome gift could not be updated. <a href="' + root + 'cart">View your cart</a>';
    let failed = false;
    try {
      if (Object.keys(plan.updates).length) {
        const data = await post('cart/update.js', {updates: plan.updates});
        if (!render(data.sections) && !(await refresh())) failed = true;
      }
      if (!failed && plan.additions.length) {
        const data = await post('cart/add.js', {items: plan.additions});
        if (!render(data.sections) && !(await refresh())) failed = true;
      }
    } catch (_) {
      failed = true;
    }
    settledRender = renders;
    const fresh = giftPlan(giftState());
    giftBlocked = failed || plan.charged || !fresh || fresh.pending;
    if (giftBlocked || plan.unavailable) showNotice(notice);
    applyControls();
  }
  function showNotice(html) {
    const target = dialog() && dialog().querySelector('[data-ap-cart-error]');
    if (!target) return;
    target.innerHTML = html;
    target.hidden = false;
  }

  // While a cart request runs, the drawer controls and checkout are disabled, so checkout cannot race an update.
  function applyControls() {
    const d = dialog();
    if (!d) return;
    d.setAttribute('aria-busy', String(busy));
    d.querySelectorAll('[data-cart-change], [data-plan-change]').forEach(b => { b.disabled = busy; });
    d.querySelectorAll('[data-plan-trigger]').forEach(b => b.setAttribute('aria-disabled', String(busy)));
    d.querySelectorAll('.bag-store-link').forEach(b => { b.disabled = busy || giftBlocked || tierBlocked || stale; });
  }

  function setBusy(on) {
    busy = on;
    applyControls();
  }

  /* items: [{id, quantity, selling_plan?, properties?}]. Resolves {status: 'added', rendered} after a successful add,
     or {status: 'busy'} when another cart request is running (nothing was sent). Rejects when the add failed. */
  async function add(items, errorTarget, from) {
    if (busy) return {status: 'busy'};
    const returnFocus = from || document.activeElement;
    setBusy(true);
    let data;
    try {
      data = await post('cart/add.js', {items});
    } catch (error) {
      setBusy(false);
      showError(error.message, errorTarget);
      throw error;
    }
    let rendered = render(data.sections);
    if (!rendered) rendered = await refresh();
    if (rendered) await reconcileCart();
    setBusy(false);
    document.dispatchEvent(new CustomEvent('ap:cart-added', {detail: {items, rendered}}));
    if (rendered) {
      open(returnFocus);
    } else {
      stale = true; // the next open refreshes before checkout is allowed
      applyControls();
    }
    if (!rendered && errorTarget) {
      // The item is in the cart, but the drawer could not be refreshed: say so and offer the cart page.
      errorTarget.innerHTML = `Added to your bag. <a href="${root}cart">View your cart</a>`;
      errorTarget.hidden = false;
    }
    return {status: 'added', rendered, data};
  }

  async function change(key, quantity, control) {
    if (busy) return;
    const line = dialog()?.querySelector(`.bag-line[data-key="${CSS.escape(key)}"]`);
    if (!line || !Number.isInteger(quantity) || quantity < 0) {
      showError('This quantity could not be updated. Please refresh your bag.');
      return;
    }
    const wasPlus = control && control.getAttribute('aria-label') === 'Add one';
    const variant = line.dataset.variant;
    const properties = line.dataset.properties;
    const subscription = Boolean(line.dataset.sellingPlan);
    setBusy(true);
    try {
      const data = await post('cart/change.js', {id: key, quantity});
      if (render(data.sections) || (await refresh())) {
        await reconcileCart();
      } else {
        // The change succeeded but the drawer could not be refreshed: it may show an old bag.
        const target = dialog() && dialog().querySelector('[data-ap-cart-error]');
        if (target) {
          target.innerHTML = `Your bag was updated, but this list may be out of date. <a href="${root}cart">View your cart</a>`;
          target.hidden = false;
        }
        // Count paid bottles only; gift variants come from the last drawer state.
        const state = giftState();
        const giftIds = new Set([...(state ? state.lines : []), ...(state ? state.entitled : [])].map(g => Number(g.id)));
        if (Array.isArray(data.items)) setCount(data.items.filter(i => !giftIds.has(Number(i.variant_id))).reduce((n, i) => n + i.quantity, 0));
        stale = true;
        applyControls();
      }
      document.dispatchEvent(new CustomEvent('ap:cart-changed', {detail: {key, quantity}}));
    } catch (error) {
      if (await refresh()) await reconcileCart();
      else stale = true;
      showError(error.message);
    } finally {
      setBusy(false);
      const d = dialog();
      if (d && (d.open || d.hasAttribute('data-cart-page'))) {
        // Keep focus on the same control after the re-render; fall back to the line, then the close button.
        const line = d.querySelector(`.bag-line[data-key="${CSS.escape(key)}"]`)
          || [...d.querySelectorAll('.bag-line')].find(item => item.dataset.variant === variant && item.dataset.properties === properties && Boolean(item.dataset.sellingPlan) === subscription);
        const same = line && line.querySelector(wasPlus ? '[aria-label="Add one"]' : quantity === 0 ? null : '[aria-label="Remove one"]');
        (same || line?.querySelector('[data-cart-change]') || d.querySelector('.bag-store-link, .bag-continue') || d.querySelector('.dialog-close'))?.focus();
      }
    }
  }

  /* Purchase plan of a cart line. The line index comes from a fresh /cart.js read (never the drawer order: gift lines
     sit elsewhere in the cart); one change.js applies the selected plan's bottle quantity and keeps the line
     properties, with the purchase type property updated to match the plan. The gift state is reconciled afterwards. */
  async function changePlan(key, plan, control) {
    if (busy) return;
    const typeKey = (window.theme && window.theme.product_type_key) || '';
    const panel = control && control.closest('.bag-plan');
    const previous = panel && [...panel.querySelectorAll('[data-plan-change]')].find(r => r.defaultChecked);
    setBusy(true);
    let variantId = null;
    let failure = null;
    let focusKey = key; // unchanged line after a failed change
    try {
      const response = await fetch(root + 'cart.js', {headers: {Accept: 'application/json'}, cache: 'no-store'});
      if (!response.ok) throw new Error('Your bag could not be read. Please try again.');
      const cart = await response.json();
      const index = cart.items.findIndex(i => i.key === key);
      if (index < 0) throw new Error('This item changed. The bag has been refreshed.');
      const item = cart.items[index];
      variantId = item.variant_id;
      // Keep every line property; only the purchase-type value follows the new plan.
      const properties = {...(item.properties || {})};
      if (typeKey && typeKey in properties) properties[typeKey] = plan ? 'Subscription' : 'Single';
      const quantity = plan ? Number(control?.dataset.planQuantity) : item.quantity;
      if (!Number.isInteger(quantity) || quantity < 1) throw new Error('The plan quantity could not be read.');
      const data = await post('cart/change.js', {line: index + 1, quantity, selling_plan: plan ? Number(plan) : null, properties});
      // The changed line in the response: same variant, the new plan and exactly the preserved properties (a key
      // changes with the plan; identical lines may merge). Prefer the same position when several match.
      const same = (a, b2) => JSON.stringify(Object.entries(a || {}).sort()) === JSON.stringify(Object.entries(b2 || {}).sort());
      const planId = i => (i.selling_plan_allocation && i.selling_plan_allocation.selling_plan && i.selling_plan_allocation.selling_plan.id) || null;
      const matches = (data.items || []).filter(i => i.variant_id === item.variant_id && String(planId(i) || '') === String(plan || '') && same(i.properties, properties));
      const changed = matches.find(i => data.items.indexOf(i) === index) || matches[0];
      if (changed) focusKey = changed.key;
      if (render(data.sections) || (await refresh())) {
        await reconcileCart();
      } else {
        stale = true;
        failure = 'Your plan was changed, but this list may be out of date.';
      }
      document.dispatchEvent(new CustomEvent('ap:cart-changed', {detail: {key, plan}}));
    } catch (error) {
      // Failed or unknown outcome: show the real cart (never repeat the change) and settle its gift state.
      failure = error && error.known ? error.message : 'Your plan may not have changed. Check your bag before checkout.';
      if (await refresh()) {
        await reconcileCart();
      } else {
        stale = true;
        if (previous) previous.checked = true; // the drawer could not be re-read: show the last known plan
      }
    } finally {
      if (failure) showError(failure);
      setBusy(false);
      applyControls();
      // Focus the plan control of the changed line (its new key), else of the unchanged line, else a safe control.
      const d = dialog();
      if (d && (d.open || d.hasAttribute('data-cart-page'))) {
        const target = d.querySelector(`.bag-line[data-key="${CSS.escape(focusKey)}"] [data-plan-trigger]`)
          || d.querySelector(`.bag-line[data-variant="${variantId}"] [data-plan-trigger]`);
        (target || d.querySelector('[data-plan-trigger]') || d.querySelector('.dialog-close'))?.focus({preventScroll: true});
      }
    }
  }

  document.addEventListener('submit', e => {
    if (!e.target.matches('#bag-dialog .bag-checkout')) return;
    if (busy || giftBlocked || tierBlocked || stale) {
      e.preventDefault();
    }
  });

  document.addEventListener('change', e => {
    const radio = e.target.closest && e.target.closest('[data-plan-change]');
    if (radio && radio.checked && dialog() && dialog().contains(radio)) changePlan(radio.dataset.planChange, radio.value, radio);
  });
  // Escape closes an open plan panel first; the next Escape closes the drawer.
  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    const panel = e.target.closest && e.target.closest('#bag-dialog .bag-plan[open]');
    if (!panel) return;
    e.preventDefault();
    e.stopPropagation();
    panel.open = false;
    panel.querySelector('summary').focus();
  }, true);

  document.addEventListener('click', e => {
    const trigger = e.target.closest && e.target.closest('[data-plan-trigger]');
    if (trigger) {
      if (busy) { e.preventDefault(); return; }
      dialog()?.querySelectorAll('.bag-plan[open]').forEach(panel => { if (panel !== trigger.parentElement) panel.open = false; });
    }
    const bag = e.target.closest && e.target.closest('[data-open-bag]');
    if (bag && dialog()) { e.preventDefault(); open(); return; }
    const d = dialog();
    if (!d || !d.contains(e.target)) return;
    if (e.target.closest('[data-close], [data-bag-close]')) { e.preventDefault(); close(); return; }
    const changer = e.target.closest('[data-cart-change]');
    if (changer) { e.preventDefault(); if (!changer.disabled) change(changer.dataset.cartChange, Number(changer.dataset.quantity), changer); return; }
    if (e.target === d) {
      const r = d.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) close();
    }
  });

  function bindDialog() {
    const d = dialog();
    if (!d || d.dataset.apCartBound) return;
    d.dataset.apCartBound = 'true';
    d.addEventListener('close', () => {
      lock(false);
      if (opener && opener.isConnected) opener.focus({preventScroll: true});
      opener = null;
    });
    setCount(d.dataset.itemCount || 0);
    applyControls();
    if (d.hasAttribute('data-cart-page')) {
      setBusy(true);
      reconcileCart().finally(() => setBusy(false));
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindDialog, {once: true}); else bindDialog();

  window.apCart = {add, change, open, close, render};
})();
